import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import Button from "@insite/mobius/Button";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon from "@insite/mobius/Icon";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import get from "@insite/mobius/utilities/get";
import { getIsSwatchesPlp, getSwatchCartCount } from "@tSurya/Common/Selectors/swatchCartSelectors";
import { observeStickyCheckoutOffset } from "@tSurya/Common/Utilities/stickyCheckoutOffset";
import { isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import CustomArrowUp from "@tSurya/Icons/CustomArrowUp";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const SCROLL_THRESHOLD = 300;

export interface BackToTopStyles {
    button?: ButtonPresentationProps;
}

export const backToTopStyles: BackToTopStyles = {
    button: {
        css: css`
            width: 48px;
            height: 48px;
            border-radius: 50%;
            display: flex;
            justify-content: center;
            align-items: center;
            position: fixed;
            bottom: var(--back-to-top-bottom, 30px);
            right: 22px;
            background-color: rgb(81 73 81) !important;
            border: rgb(81 73 81) !important;
            z-index: ${({ theme }: { theme: BaseTheme }) => get(theme, "zIndex.stickyFooter") + 1};
            opacity: 0;
            pointer-events: none;
            transition:
                opacity 0.3s ease-in-out,
                bottom 0.3s ease-in-out;

            &[data-visible="true"] {
                opacity: 1;
                pointer-events: auto;
            }

            &[data-swatch-active="true"] {
                bottom: 110px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            bottom: 220px;
                        `,
                        css`
                            bottom: 220px;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }

            &[data-mgbw-pdp-active="true"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            bottom: 150px;
                        `,
                        css`
                            bottom: 150px;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
        `,
    },
};

const styles = backToTopStyles;

const mapStateToProps = (state: CustomApplicationState) => {
    const currentPage = getCurrentPage(state);
    return {
        swatchMiniCartActive: getIsSwatchesPlp(state) && getSwatchCartCount(state) > 0,
        mgbwPdpActive: currentPage.type === "ProductDetailsPage" && isMgbwWebsite(),
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const BackToTop: React.FC<Props> = ({ swatchMiniCartActive, mgbwPdpActive }) => {
    const [visible, setVisible] = useState(false);
    const [stickyActionOffset, setStickyActionOffset] = useState(0);

    const handleScroll = useCallback(() => {
        setVisible(window.scrollY > SCROLL_THRESHOLD);
    }, []);

    useEffect(() => {
        window.addEventListener("scroll", handleScroll, { passive: true });
        handleScroll();
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    useEffect(
        () => observeStickyCheckoutOffset(height => setStickyActionOffset(height > 0 ? Math.ceil(height) + 20 : 0)),
        [],
    );

    const scrollToTop = useCallback(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    return (
        <Button
            {...styles.button}
            onClick={scrollToTop}
            aria-label="Back to top"
            data-visible={visible}
            data-swatch-active={swatchMiniCartActive}
            data-mgbw-pdp-active={mgbwPdpActive}
            style={{ "--back-to-top-bottom": stickyActionOffset ? `${stickyActionOffset}px` : '30px' } as React.CSSProperties}
            data-test-selector="backToTopButton"
        >
            <Icon src={CustomArrowUp} />
        </Button>
    );
};

export default connect(mapStateToProps)(React.memo(BackToTop));
